import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import type { AnyEntity, BackendOrSurfaceEntity, IntegrationEntity, ClientEntity } from "../api/types";
import { StatAlerts, StatSlos, StatRunbooks, StatDeps, StatQps, StatFreshness } from "./Icons";

export function EntityCard({ item }: { item: AnyEntity }) {
  const ui = useUI();
  const data = useData();
  const tier = "tier" in item ? item.tier : null;
  const cov = data.coverageByName[item.name];

  const open = () => ui.openEntity(item);
  return (
    // role=button (not <button>) so the env-link <a>s below are valid children —
    // interactive elements can't nest inside a native <button>.
    <div className={`card${tier ? ` tier-${tier}` : ""}`}
         role="button" tabIndex={0}
         aria-label={`${item.name}${tier ? ` (${tier})` : ""} — open details`}
         onClick={open}
         onKeyDown={(e) => {
           if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
         }}>
      <div className="card-head">
        <div className="card-name">{item.name}</div>
        <div className="card-meta">
          {tier && <span className={`tier-badge tier-${tier}`} title="Criticality tier — P0 (most critical: customer-facing money path, page immediately) · P1 high · P2 medium · P3 low" style={{ cursor: "help" }}>{tier}</span>}
          {cov && typeof cov.health_score === "number" && <ScoreBadge score={cov.health_score} />}
        </div>
      </div>
      <CardMeta item={item} />
      {"notes" in item && item.notes && (
        <div className="card-desc">{truncate(item.notes, 120)}</div>
      )}
      <EnvBadges item={item} />
      <CardStats item={item} />
    </div>
  );
}

function CardMeta({ item }: { item: AnyEntity }) {
  const bits: React.ReactNode[] = [];
  if ("type" in item && item.type) bits.push(<span key="t" className="kind-badge">{item.type}</span>);
  if ("kind" in item && item.kind) bits.push(<span key="k" className="kind-badge">{item.kind}</span>);
  if (item.owner_team) {
    bits.push(<span key="o" style={{ color: "var(--text-muted)", fontSize: 11.5 }}>{item.owner_team}</span>);
  }
  if (bits.length === 0) return null;
  return <div className="card-meta">{bits}</div>;
}

function CardStats({ item }: { item: AnyEntity }) {
  const bits: React.ReactNode[] = [];
  if (isBE(item)) {
    if (item.alerts_count > 0)             bits.push(stat("a", <StatAlerts className="stat-icon" size={13} />, item.alerts_count, "alerts"));
    if (item.slos_count > 0)               bits.push(stat("s", <StatSlos className="stat-icon" size={13} />, item.slos_count, "SLOs"));
    if (item.runbooks?.length)             bits.push(stat("r", <StatRunbooks className="stat-icon" size={13} />, item.runbooks.length, "runbooks"));
    if (item.dependencies?.length)         bits.push(stat("d", <StatDeps className="stat-icon" size={13} />, item.dependencies.length, "deps"));
  } else if (isI(item)) {
    if (item.expected_freshness)           bits.push(stat("f", <StatFreshness className="stat-icon" size={13} />, item.expected_freshness, ""));
  } else if (isC(item)) {
    if (item.expected_qps != null)         bits.push(stat("q", <StatQps className="stat-icon" size={13} />, item.expected_qps, "qps"));
  }
  if (bits.length === 0) return null;
  return <div className="card-stats">{bits}</div>;
}

function EnvBadges({ item }: { item: AnyEntity }) {
  if (!isBE(item) || !item.urls) return null;
  const entries = Object.entries(item.urls).filter(([, v]) => v) as [string, string][];
  if (entries.length === 0) return null;
  return (
    <div className="env-badges">
      {entries.map(([env, url]) => (
        <a key={env} className={`env-badge ${env}`}
           href={url} target="_blank" rel="noopener noreferrer"
           title={url}
           onClick={(e) => e.stopPropagation()}>{env}</a>
      ))}
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  let cls = "score-strong"; let label = "strong";
  if (score < 50)      { cls = "score-poor";   label = "poor"; }
  else if (score < 70) { cls = "score-fair";   label = "fair"; }
  else if (score < 90) { cls = "score-good";   label = "good"; }
  return (
    <span className={`score-badge ${cls}`} title={`Health ${score}/100 — ${label}`}>
      <span className="score-dot" />{score}
    </span>
  );
}

function stat(key: string, icon: React.ReactNode, num: React.ReactNode, label: string) {
  return (
    <span key={key}>{icon} <span className="num">{num}</span>{label && ` ${label}`}</span>
  );
}
function truncate(s: string, n: number) { return !s ? "" : s.length <= n ? s : s.slice(0, n - 1) + "…"; }
function isBE(it: AnyEntity): it is BackendOrSurfaceEntity { return "dependencies" in it && "alerts_count" in it; }
function isI(it: AnyEntity):  it is IntegrationEntity        { return "kind" in it && "webhook_endpoint" in it; }
function isC(it: AnyEntity):  it is ClientEntity             { return "type" in it && (it.type === "partner" || it.type === "sdk-consumer" || it.type === "end-user-cohort"); }
